/**
 * equipe.ts — Categoria "Equipe" (10 modelos premium)
 *
 * Apresentação das pessoas por trás do serviço, com fotos do acervo
 * compartilhado, números de credibilidade e chamadas de ação.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - 10 modelos novos (Studio, motor de modelos — Lote B)
 */

import { registerPatterns } from './patternRegistry';
import type { StudioPatternDefinition } from './types';
import { FOTO, WEB, b } from './_shared';

/** Atalho para o bloco de equipe. */
function equipe(itens: string, colunas = '4', titulo = '') {
  return b('page.team', { title: titulo, items: itens, columns: colunas });
}

const QUATRO = [
  `${FOTO.retrato}|Ana Martins|Diretora de operações`,
  `${FOTO.equipe}|Carlos Ferreira|Gerente de projetos`,
  `${FOTO.atendimento}|Juliana Prado|Líder de atendimento`,
  `${FOTO.consultoria}|Rafael Souza|Consultor sênior`,
].join('\n');

const TRES = QUATRO.split('\n').slice(0, 3).join('\n');

const modelos: StudioPatternDefinition[] = [
  {
    key: 'equipe.quatro',
    category: 'equipe',
    label: 'Quatro pessoas',
    description: 'Grade com quatro integrantes e cargos.',
    preview: 'grid-4',
    outputs: WEB,
    order: 1,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1120px', motion: 'fade-up' }, [
        b('ui.title', { text: 'Quem cuida de você', size: 'lg', level: 'h2', align: 'center' }),
        equipe(QUATRO),
      ]),
    ],
  },

  {
    key: 'equipe.tres',
    category: 'equipe',
    label: 'Três pessoas',
    description: 'Time enxuto, com fotos maiores.',
    preview: 'grid-3',
    outputs: WEB,
    order: 2,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '960px', backgroundColor: '#f8fafc', motion: 'fade' }, [
        equipe(TRES, '3', 'O time responsável pela sua conta'),
      ]),
    ],
  },

  {
    key: 'equipe.com-texto',
    category: 'equipe',
    label: 'Equipe com apresentação',
    description: 'Texto de abertura acima das fotos do time.',
    preview: 'grid-4',
    outputs: WEB,
    order: 3,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1120px', motion: 'fade-up' }, [
        b('ui.badge', { text: 'Nosso time', variant: 'soft', align: 'center' }),
        b('ui.title', { text: 'Gente que conhece o seu negócio pelo nome', size: 'lg', level: 'h2', align: 'center' }),
        b('ui.paragraph', { text: 'Cada cliente tem um responsável direto, com apoio de especialistas por área.', align: 'center', maxWidth: '620px' }),
        equipe(QUATRO),
      ]),
    ],
  },

  {
    key: 'equipe.escura',
    category: 'equipe',
    label: 'Equipe em fundo escuro',
    description: 'Fundo escuro com brilho e fotos em destaque.',
    preview: 'grid-4',
    outputs: WEB,
    order: 4,
    build: () => [
      b(
        'section.layered',
        { paddingY: 'xl', align: 'center', maxWidth: '1120px', backgroundGradient: 'mesh-dark', glow: 'top-right', textColor: '#ffffff', motion: 'fade' },
        [b('ui.title', { text: 'Especialistas à sua disposição', size: 'lg', level: 'h2', align: 'center', accentColor: '#7dd3fc' }), equipe(QUATRO)],
      ),
    ],
  },

  {
    key: 'equipe.com-numeros',
    category: 'equipe',
    label: 'Equipe com números',
    description: 'Tamanho do time e experiência em destaque.',
    preview: 'stats',
    outputs: WEB,
    order: 5,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1120px', layout: 'bento', motion: 'fade-up' }, [
        b('ui.stat', { value: '120', label: 'profissionais no time', align: 'center' }),
        b('ui.stat', { value: '12 anos', label: 'de experiência média', align: 'center' }),
        equipe(TRES, '3'),
      ]),
    ],
  },

  {
    key: 'equipe.lider',
    category: 'equipe',
    label: 'Liderança em destaque',
    description: 'Uma pessoa em foco com depoimento ao lado.',
    preview: 'split-left',
    outputs: WEB,
    order: 6,
    build: () => [
      b('section.layered', { layout: 'split-reverse', paddingY: 'lg', align: 'left', maxWidth: '1000px', motion: 'fade' }, [
        b('ui.image', { src: FOTO.retrato, alt: 'Diretora de operações', ratio: 'square', radius: 'full', shadow: 'lg' }),
        b('ui.title', { text: '“Nosso compromisso é com o resultado do cliente.”', size: 'lg', level: 'h2' }),
        b('ui.paragraph', { text: 'Ana Martins — Diretora de operações', size: 'sm' }),
      ]),
    ],
  },

  {
    key: 'equipe.com-foto-ambiente',
    category: 'equipe',
    label: 'Equipe com foto do ambiente',
    description: 'Foto do escritório acompanha a apresentação do time.',
    preview: 'split-right',
    outputs: WEB,
    order: 7,
    build: () => [
      b('section.layered', { layout: 'split', paddingY: 'lg', align: 'left', maxWidth: '1120px', backgroundColor: '#f8fafc', motion: 'fade-up' }, [
        b('ui.title', { text: 'Um time perto de você', size: 'lg', level: 'h2' }),
        b('ui.paragraph', { text: 'Estrutura própria, processos claros e pessoas preparadas para atender.' }),
        b('ui.image', { src: FOTO.time, alt: 'Equipe no escritório', ratio: 'landscape', radius: 'xl', shadow: 'lg' }),
        equipe(TRES, '3'),
      ]),
    ],
  },

  {
    key: 'equipe.com-cta',
    category: 'equipe',
    label: 'Equipe com convite',
    description: 'Fotos do time seguidas de botão para conversar.',
    preview: 'cta',
    outputs: WEB,
    order: 8,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1080px', motion: 'fade' }, [
        equipe(QUATRO, '4', 'Fale com quem vai te atender'),
        b('ui.buttons', { items: 'Agendar uma conversa|#formulario|solid', align: 'center', size: 'lg' }),
      ]),
    ],
  },

  {
    key: 'equipe.sobre-foto',
    category: 'equipe',
    label: 'Equipe sobre foto',
    description: 'Foto de ambiente ao fundo com o time por cima.',
    preview: 'grid-3',
    outputs: WEB,
    order: 9,
    build: () => [
      b(
        'section.layered',
        { backgroundImage: FOTO.escritorio, overlay: 'darker', paddingY: 'xl', align: 'center', textColor: '#ffffff', maxWidth: '1080px', motion: 'fade-up' },
        [b('ui.title', { text: 'Pessoas antes de processos', size: 'lg', level: 'h2', align: 'center' }), equipe(TRES, '3')],
      ),
    ],
  },

  {
    key: 'equipe.compacta',
    category: 'equipe',
    label: 'Equipe compacta',
    description: 'Versão curta, boa para páginas institucionais enxutas.',
    preview: 'grid-3',
    outputs: WEB,
    order: 10,
    build: () => [
      b('section.layered', { paddingY: 'md', align: 'center', maxWidth: '900px', motion: 'fade' }, [equipe(TRES, '3')]),
    ],
  },
];

registerPatterns(modelos);
